"use client";

import { cn } from "@istari-frontend/shared";
import { type VariantProps } from "class-variance-authority";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import * as React from "react";
import { useContext } from "react";

import { raisedItemVariants, type RaisedSize, raisedTrackVariants } from "./raised-variants";
import { toggleVariants } from "./toggle";

type ToggleGroupStyle = VariantProps<typeof toggleVariants> | { variant: "raised"; size?: RaisedSize };

const ToggleGroupContext = React.createContext<ToggleGroupStyle>({
  size: "default",
  variant: "default",
});

export function ToggleGroup({
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & ToggleGroupStyle) {
  if (variant === "raised") {
    return <ToggleGroupRoot style={{ variant, size }} trackClassName={raisedTrackVariants({ size })} {...props} />;
  }

  return <ToggleGroupRoot style={{ variant, size }} trackClassName="flex w-fit items-center rounded-md" {...props} />;
}

export function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  const context = useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      className={cn(toggleGroupItemClassName(context, { variant, size }), className)}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

function toggleGroupItemClassName(context: ToggleGroupStyle, item: VariantProps<typeof toggleVariants>) {
  if (context.variant === "raised") return raisedItemVariants({ size: context.size });

  return cn(
    toggleVariants({
      variant: context.variant || item.variant,
      size: context.size || item.size,
    }),
    "min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l",
  );
}

function ToggleGroupRoot({
  style,
  trackClassName,
  className,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & { style: ToggleGroupStyle; trackClassName: string }) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={style.variant}
      data-size={style.size}
      className={cn("group/toggle-group", trackClassName, className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={style}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}
